import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../contexts/app-context';
import {
  useReportRouteLoading,
  useRetryRoute,
} from '../../hooks/use-route-loading';
import { uicolors } from '../../theme';
import { AnimateInProps } from '../animate-in';
import { AppHeader } from '../app-header';
import { AllianceFooter } from '../alliance/alliance-footer';
import { FooterAdCarousel } from '../footer-ad-carousel';
import { useSessionActions } from '../../hooks/use-session-actions';
import { useAgentAccessDenied } from '../alliance/agent-access';
import { LoadFailedScreen, NoAgentScreen } from '../alliance/system-screens';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
});

/**
 * Wraps every top-level page: the document title, the page header (title and
 * header content such as the impersonation search), then the content, the
 * load error or the error boundary, with the ad carousel and footer.
 *
 * While `loading`, the header stays and the content area is empty; the shell
 * shows the 3px bar under the App Bar after 150ms. `hasError` shows
 * "Couldn't load this page" (offline vs. server trouble) with Try again,
 * which refetches failed queries and remounts the page. While impersonating,
 * if every request for that agent was refused (403), the page shows "You
 * don't have access to this agent's data" with Stop impersonating; the
 * header (and its search) stays.
 */
export function RouteContainer(props: {
  children: React.ReactNode;
  routeTitle: string;
  loading?: boolean;
  headerContent?: React.ReactNode;
  wrapContentForMobile?: boolean;
  hasError?: boolean;
  paddingBottom?: number;
  hideHeader?: boolean;
  hideFooter?: boolean;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  darkMode?: boolean;
}) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  // Hooks
  const classes = useStyles();
  const retryRoute = useRetryRoute();
  const { stopImpersonating } = useSessionActions();
  const impersonatedAgent = state.impersonatedAgent;
  const agentDenied = useAgentAccessDenied(impersonatedAgent?.agentno);
  useReportRouteLoading({ loading: !!props.loading });
  // Hide footer carousel for admin pages
  const location = useLocation();
  const isAdminPath = /^\/admin(\/|$)/.test(location.pathname);

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
  }, [dispatch]);

  let routeContent: JSX.Element | null = null;
  if (impersonatedAgent && agentDenied) {
    // Every request for the impersonated agent came back 403
    routeContent = (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <NoAgentScreen
          agent={{
            name:
              [impersonatedAgent.fname, impersonatedAgent.lname]
                .filter(Boolean)
                .join(' ') ||
              impersonatedAgent.agentname ||
              impersonatedAgent.agentno,
            id: impersonatedAgent.agentno,
          }}
          onStopImpersonating={stopImpersonating}
        />
      </div>
    );
  } else if (props.loading) {
    routeContent = null;
  } else if (props.hasError) {
    routeContent = (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <LoadFailedScreen
          layout="inline"
          onRetry={retryRoute}
          inShell={!isAdminPath}
        />
      </div>
    );
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          footer={
            // No admin page has a footer
            isAdminPath || props.hideFooter ? undefined : <AllianceFooter />
          }
        >
          {props.children}

          {isAdminPath || props.hideFooter ? null : <FooterAdCarousel />}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>ARC · {props.routeTitle}</title>
      </Helmet>

      <div
        className={classes.container}
        style={{
          backgroundColor: props.darkMode ? uicolors.black[900] : 'inherit',
        }}
        role="main"
      >
        {props.hideHeader ? null : (
          <AppHeader
            routeTitle={props.routeTitle}
            headerContent={props.headerContent}
            wrapContentForMobile={props.wrapContentForMobile}
            isDarkMode={props.darkMode}
          />
        )}

        {routeContent}
      </div>
    </React.Fragment>
  );
}
